import {useEffect, useRef, useState} from 'react';
import {Button, Flex, Text, useTheme, View} from '@aws-amplify/ui-react';

const DEFAULT_MINUTES = 45;
const TEAL_RUNNING = 'hsl(162, 50%, 30%)';
const TEAL_BORDER = 'hsl(162, 20%, 88%)';
const TEAL_INPUT_BORDER = 'hsl(162, 50%, 40%)';
const TEAL_BUTTON = 'hsl(162, 50%, 35%)';

const inputStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    textAlign: 'center',
    fontFamily: 'monospace',
    fontWeight: 700,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    color: TEAL_RUNNING,
    width: '3rem',
    padding: '0',
};

function pad(n: number): string {
    return String(Math.max(0, n)).padStart(2, '0');
}

function DoneModal({onClose}: { onClose: () => void }) {
    return (
        <div style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
        }}>
            <div style={{
                background: '#fff', borderRadius: '16px',
                padding: '2.5rem 2rem', maxWidth: '300px', width: '90%',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}>
                <div style={{fontSize: '2.5rem', marginBottom: '0.75rem'}}>⏰</div>
                <div style={{fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1a1a'}}>
                    Time to take a break
                </div>
                <div style={{color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem'}}>
                    Hit Go! to pick a break activity.
                </div>
                <button onClick={onClose} style={{
                    background: TEAL_BUTTON, color: '#fff',
                    border: 'none', borderRadius: '8px',
                    padding: '0.6rem 2rem', fontSize: '1rem',
                    cursor: 'pointer', fontWeight: 600,
                }}>
                    OK
                </button>
            </div>
        </div>
    );
}

interface WorkTimerProps {
    onStart?: () => void;
}

export default function WorkTimer({onStart}: WorkTimerProps) {
    const {tokens} = useTheme();
    const [seconds, setSeconds] = useState(DEFAULT_MINUTES * 60);
    const [isRunning, setIsRunning] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [inputMins, setInputMins] = useState(String(DEFAULT_MINUTES));
    const [inputSecs, setInputSecs] = useState('0');
    const [showModal, setShowModal] = useState(false);
    const minsRef = useRef<HTMLInputElement>(null);
    const secsRef = useRef<HTMLInputElement>(null);

    // Countdown using functional updater to avoid stale closure
    useEffect(() => {
        if (!isRunning) return;
        const id = setInterval(() => setSeconds(s => s - 1), 1000);
        return () => clearInterval(id);
    }, [isRunning]);

    function playBellSound() {
        try {
            const ctx = new AudioContext();
            const now = ctx.currentTime;
            [0, 0.1, 0.2].forEach((delay, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.value = i === 0 ? 880 : i === 1 ? 1100 : 1320;
                gain.gain.setValueAtTime(0.4, now + delay);
                gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 1.2);
                osc.start(now + delay);
                osc.stop(now + delay + 1.2);
            });
        } catch {
            // AudioContext not supported
        }
    }

    // Detect timer reaching zero in a separate effect
    useEffect(() => {
        if (isRunning && seconds <= 0) {
            setIsRunning(false);
            setShowModal(true);
            playBellSound();
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('Time to take a break', {
                    body: 'Hit Go! to pick a break activity.',
                    requireInteraction: true,
                });
            }
        }
    }, [seconds, isRunning]);

    useEffect(() => {
        if (isEditing) minsRef.current?.focus();
    }, [isEditing]);

    function handleTimerClick() {
        if (!isRunning) {
            const m = Math.floor(Math.max(0, seconds) / 60);
            const s = Math.max(0, seconds) % 60;
            setInputMins(String(m).padStart(2, '0'));
            setInputSecs(String(s).padStart(2, '0'));
            setIsEditing(true);
        }
    }

    function padNum(val: string, max: number): string {
        const n = Math.min(max, Math.max(0, parseInt(val, 10) || 0));
        return String(n).padStart(2, '0');
    }

    function commitEdit(minsVal = inputMins, secsVal = inputSecs) {
        const m = Math.min(90, Math.max(0, parseInt(minsVal, 10) || 0));
        const s = Math.min(59, Math.max(0, parseInt(secsVal, 10) || 0));
        const total = Math.max(1, m * 60 + s);
        setSeconds(total);
        setIsEditing(false);
    }

    function jumpToSecs() {
        const padded = padNum(inputMins, 90);
        setInputMins(padded);
        secsRef.current?.focus();
        secsRef.current?.select();
    }

    function jumpToMins() {
        const padded = padNum(inputSecs, 59);
        setInputSecs(padded);
        minsRef.current?.focus();
        minsRef.current?.select();
    }

    function handleMinsKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Tab') {
            e.preventDefault();
            jumpToSecs();
        } else if (e.key === 'ArrowRight' && e.currentTarget.selectionStart === e.currentTarget.value.length) {
            e.preventDefault();
            jumpToSecs();
        } else if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }

    function handleSecsKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowLeft' && e.currentTarget.selectionStart === 0) {
            e.preventDefault();
            jumpToMins();
        } else if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }

    function handleStartStop() {
        const starting = !isRunning;
        if (starting) {
            onStart?.();
            if ('Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission();
            }
        }
        setIsRunning(starting);
    }

    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.max(0, seconds) % 60;

    return (
        <>
            {showModal && (
                <DoneModal onClose={() => {
                    setShowModal(false);
                    setSeconds(DEFAULT_MINUTES * 60);
                }}/>
            )}
            <View
                borderRadius="12px"
                padding={tokens.space.medium}
                marginBottom={tokens.space.medium}
                backgroundColor={tokens.colors.background.secondary}
                style={{border: `1px solid ${TEAL_BORDER}`}}
            >
                <Flex direction="row" alignItems="center" justifyContent="center" gap={tokens.space.large}>
                    {isEditing ? (
                        <div style={{
                            display: 'inline-flex', alignItems: 'center',
                            border: `2px solid ${TEAL_INPUT_BORDER}`,
                            borderRadius: '8px', padding: '2px 8px',
                            background: '#fff',
                        }}>
                            <input
                                ref={minsRef}
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={inputMins}
                                onChange={e => {
                                    const v = e.target.value.replace(/\D/g, '');
                                    setInputMins(v);
                                    if (v.length === 2) {
                                        secsRef.current?.focus();
                                        secsRef.current?.select();
                                    }
                                }}
                                onBlur={() => {
                                    setTimeout(() => {
                                        if (document.activeElement !== secsRef.current &&
                                            document.activeElement !== minsRef.current) {
                                            commitEdit();
                                        }
                                    }, 100);
                                }}
                                onFocus={e => e.target.select()}
                                onKeyDown={handleMinsKeyDown}
                                style={inputStyle}
                            />
                            <span style={{
                                fontFamily: 'monospace', fontSize: '2.5rem',
                                fontWeight: 700, color: TEAL_RUNNING,
                                userSelect: 'none', lineHeight: 1,
                            }}>:</span>
                            <input
                                ref={secsRef}
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={inputSecs}
                                onChange={e => setInputSecs(e.target.value.replace(/\D/g, ''))}
                                onBlur={() => {
                                    setTimeout(() => {
                                        if (document.activeElement !== minsRef.current &&
                                            document.activeElement !== secsRef.current) {
                                            commitEdit();
                                        }
                                    }, 100);
                                }}
                                onFocus={e => e.target.select()}
                                onKeyDown={handleSecsKeyDown}
                                style={inputStyle}
                            />
                        </div>
                    ) : (
                        <Text
                            fontSize={tokens.fontSizes.xxxl}
                            fontWeight={tokens.fontWeights.bold}
                            onClick={handleTimerClick}
                            title={isRunning ? undefined : 'Click to set time'}
                            style={{
                                fontFamily: 'monospace',
                                letterSpacing: '0.05em',
                                cursor: isRunning ? 'default' : 'pointer',
                                color: isRunning ? TEAL_RUNNING : undefined,
                                userSelect: 'none',
                            }}
                        >
                            {pad(mins)}:{pad(secs)}
                        </Text>
                    )}
                    <Button
                        variation="primary"
                        borderRadius="8px"
                        onClick={handleStartStop}
                    >
                        {isRunning ? 'Stop' : 'Start'}
                    </Button>
                </Flex>
            </View>
        </>
    );
}
